import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import {
  OUTCOME_COLORS,
  OUTCOME_LETTERS,
  VENUE_MODES,
  MODE_SHORT,
  formEntries,
  tableLimitFor,
  summariseForm,
} from '../../lib/teamForm';
import { fetchTeamForms } from '../../api/teamFormApi';
import { getTournament } from '../../api/tournaments';
import './gameDetails.css';

// What is fetched: enough for the longest mode. Each mode then shows its own count
// (tableLimitFor) - the last 10 overall, the last 5 home or away.
const FETCH_LIMIT = 10;

/**
 * Form table for every team in the game's tournament (requirements §3.6).
 *
 * Replaces the old standalone Team Form page, which searched every team in the
 * database with no tournament context - incoherent as soon as more than one league
 * exists. Here the tournament is known, so the table is scoped to it.
 */
export default function TeamFormTable({ tournamentId, tournamentName, matchWeek }) {
  const [mode, setMode] = useState(VENUE_MODES.OVERALL);
  const [teams, setTeams] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which teams have their results opened. The opponent and scoreline behind each pip
  // used to live in a title attribute, which is to say nowhere at all on a phone.
  const [openTeams, setOpenTeams] = useState(() => new Set());

  const toggleTeam = (teamId) => setOpenTeams((prev) => {
    const next = new Set(prev);
    if (next.has(teamId)) next.delete(teamId);
    else next.add(teamId);
    return next;
  });

  const load = useCallback(async () => {
    if (!tournamentId) {
      setError('No tournament linked to this game.');
      setLoading(false);
      return;
    }
    if (matchWeek == null) {
      // The server derives the form cutoff from the week, so without one there is no
      // point in time to compute form as of. Say so rather than showing form as of now.
      setError('No fixtures loaded for this week yet.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // The tournament owns its team list, so the table never shows teams from another
      // competition - or the dev-seed rows that a global /api/teams call returns (T3).
      const tournament = await getTournament(tournamentId).catch((err) => {
        throw new Error(
          err.name === 'ApiError' ? `Could not load tournament (${err.status})` : err.message
        );
      });
      const teamList = tournament.teams || [];
      setTeams(teamList);

      if (teamList.length > 0) {
        // As of this week, not as of now - see teamFormApi.js. Without the week the
        // table would show results that had not happened when the week was played.
        setForms(await fetchTeamForms(
          teamList.map((t) => t.id), tournamentId, matchWeek, FETCH_LIMIT));
      }
    } catch (err) {
      console.error('Error loading form table:', err);
      setError(err.message || 'Could not load form.');
    } finally {
      setLoading(false);
    }
  }, [tournamentId, matchWeek]);

  useEffect(() => { load(); }, [load]);

  const limit = tableLimitFor(mode);

  // Sorted by wins in the selected mode, so the table reads as a form table (T7).
  const rows = teams
    .map((team) => {
      const entries = formEntries(forms[team.id], mode, limit);
      return { team, entries, tally: summariseForm(entries) };
    })
    .sort((a, b) =>
      b.tally.W - a.tally.W ||
      b.tally.D - a.tally.D ||
      (a.team.teamName || '').localeCompare(b.team.teamName || ''));

  if (loading) {
    return (
      <div className="tf-centred">
        <RefreshCw size={28} className="tf-spin" />
        <p>Loading form…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tf-centred">
        <AlertCircle size={28} className="tf-icon" />
        <p>{error}</p>
      </div>
    );
  }

  const venueWord = mode === VENUE_MODES.HOME ? 'home' : mode === VENUE_MODES.AWAY ? 'away' : '';

  return (
    <div className="tf-wrap">
      <div className="tf-header">
        <div>
          <h3 className="tf-title">Team Form</h3>
          <p className="tf-subtitle">
            Last {limit} {venueWord && `${venueWord} `}results in {tournamentName || 'this tournament'} · oldest → newest
          </p>
        </div>
        <div className="tf-toggle-group">
          {[VENUE_MODES.OVERALL, VENUE_MODES.HOME, VENUE_MODES.AWAY].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`tf-toggle-btn${mode === m ? ' active' : ''}`}
            >
              {MODE_SHORT[m]}
            </button>
          ))}
        </div>
      </div>

      <div className="tf-draw-note">
        A draw counts against you — only a win keeps you in.
      </div>

      {/* The form column is the whole point of this table, so on a phone it must fit
          rather than sit off the right edge behind a horizontal scroll. The media
          query shrinks the pips and tightens the numeric columns to make room. */}
      <div className="tf-scroll">
        <table className="tf-table">
          <thead>
            <tr>
              <th className="tf-th is-left">Team</th>
              <th className="tf-th">W</th>
              <th className="tf-th">D</th>
              <th className="tf-th">L</th>
              <th className="tf-th is-form">
                Form {venueWord && `(${venueWord})`}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ team, entries, tally }) => {
              // Only the results that exist - no grey padding up to the limit (item 87).
              const shown = [...entries].reverse();
              const open = openTeams.has(team.id);
              return (
                <React.Fragment key={team.id}>
                <tr className="tf-row">
                  <td className="tf-team">{team.teamName}</td>
                  <td className="tf-num">{tally.W}</td>
                  <td className="tf-num">{tally.D}</td>
                  <td className="tf-num">{tally.L}</td>
                  <td className="tf-form-cell">
                    {entries.length === 0 ? (
                      <span className="tf-no-data">
                        No {venueWord ? `${venueWord} ` : ''}results yet
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="tf-pips"
                        aria-expanded={open}
                        aria-label={`Show ${team.teamName}'s results`}
                        onClick={() => toggleTeam(team.id)}
                      >
                        {shown.map((e, i) => (
                          <span
                            key={e.fixtureId ?? `f-${i}`}
                            className="tf-pip"
                            style={{ background: OUTCOME_COLORS[e.outcome] || '#ccc' }}
                          >
                            {OUTCOME_LETTERS[e.outcome]}
                          </span>
                        ))}
                        <ChevronDown size={13} className={`tf-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>

                {open && (
                  <tr className="tf-detail-row">
                    <td colSpan={5}>
                      <ul className="tf-results">
                        {/* Newest first: read as a list, the most recent result is the
                            one you want at the top - the opposite of the strip. */}
                        {entries.map((e, i) => (
                          <li key={e.fixtureId ?? `d-${i}`}>
                            <span className="tf-pip" style={{ background: OUTCOME_COLORS[e.outcome] || '#ccc' }}>
                              {OUTCOME_LETTERS[e.outcome]}
                            </span>
                            <span className="tf-result-opponent">v {e.opponentName}</span>
                            {e.teamScore != null && e.opponentScore != null && (
                              <span className="tf-result-score">{e.teamScore}–{e.opponentScore}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

